// src/core/pcm.ts — media math ported from LLABARO.py (PcmChunker + resample_linear).
// Isolated and oracle-tested because sample-rate/byte-width bugs live here.
import { LIVE_INPUT_HZ, SAMPLE_WIDTH } from "../config";

/** Frame a mono 16-bit little-endian PCM byte stream into fixed-duration chunks for the
 *  Live socket, and reassemble. Byte math: chunkBytes = hz * (ms/1000) * SAMPLE_WIDTH. */
export class PcmChunker {
  readonly chunkBytes: number;
  constructor(
    public hz: number = LIVE_INPUT_HZ,
    public chunkMs: number = 20
  ) {
    this.chunkBytes = Math.trunc(hz * (chunkMs / 1000)) * SAMPLE_WIDTH;
    if (this.chunkBytes <= 0) throw new Error("chunk too small for sample rate");
  }

  chunk(pcm: Uint8Array): Uint8Array[] {
    const out: Uint8Array[] = [];
    for (let i = 0; i < pcm.length; i += this.chunkBytes) {
      out.push(pcm.subarray(i, i + this.chunkBytes));
    }
    return out;
  }

  static join(chunks: Uint8Array[]): Uint8Array {
    const total = chunks.reduce((n, c) => n + c.length, 0);
    const out = new Uint8Array(total);
    let o = 0;
    for (const c of chunks) {
      out.set(c, o);
      o += c.length;
    }
    return out;
  }

  static sampleCount(pcm: Uint8Array): number {
    return Math.trunc(pcm.length / SAMPLE_WIDTH);
  }
}

/** Minimal linear resampler for mono 16-bit PCM. Not hi-fi, but correct-length and
 *  monotonic — which is what the port's oracle checks. Matches LLABARO.py resample_linear:
 *  n_dst = round(n_src * dst/src). Used for 48k->16k input and 24k->device output. */
export function resampleLinear(
  pcm: Int16Array,
  srcHz: number,
  dstHz: number
): Int16Array {
  if (srcHz === dstHz || pcm.length < 1) return pcm;
  const nSrc = pcm.length;
  const nDst = Math.max(1, Math.round((nSrc * dstHz) / srcHz));
  const out = new Int16Array(nDst);
  for (let i = 0; i < nDst; i++) {
    const pos = nDst > 1 ? (i * (nSrc - 1)) / (nDst - 1) : 0;
    const lo = Math.floor(pos);
    const hi = Math.min(lo + 1, nSrc - 1);
    const frac = pos - lo;
    const v = Math.round(pcm[lo] * (1 - frac) + pcm[hi] * frac);
    out[i] = Math.max(-32768, Math.min(32767, v));
  }
  return out;
}
